import { useState, useEffect } from 'react';
import { agenciesService, SubmissionUsage } from '@/services/agencies';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import toastService from '@/services/toast/service';

interface EditSubmissionUsageModalProps {
  agencyId: string;
  usage: SubmissionUsage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditSubmissionUsageModal({ agencyId, usage, open, onOpenChange, onSuccess }: EditSubmissionUsageModalProps) {
  const [used, setUsed] = useState(String(usage.used));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) setUsed(String(usage.used));
  }, [open, usage.used]);

  const parsed = Number(used);
  const isValid = used.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await agenciesService.setSubmissionUsage(agencyId, parsed);
      toastService.success('Submissions updated');
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't update submissions");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Submissions</DialogTitle>
            <DialogDescription>
              Completed submissions counted toward this month's limit.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="submissionsUsed">Submissions this month</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="submissionsUsed"
                  type="number"
                  min={0}
                  step={1}
                  value={used}
                  onChange={(e) => setUsed(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                />
                <span className="text-sm text-muted-foreground shrink-0">of {usage.limit}</span>
              </div>
              {!isValid ? (
                <p className="text-xs text-destructive">Enter a whole number, 0 or more</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Their forms stop accepting submissions at {usage.limit}. Resets to 0 on the 1st of each month.
                </p>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
